import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useMutation, UseMutationOptions, UseMutationResult, useQueryClient } from 'react-query';

import { ESignSigner, ESignSignerAuthMethod } from '@breathelife/types';

import { useDispatch } from '../../Hooks';
import { QueryId } from '../../ReactQuery/common/common.types';
import { notificationSlice } from '../../ReduxStore/Notification/NotificationSlice';
import { eSignSignerAction } from '../../Services/ESignCeremonyService';

type SignerActionBase = { signerId: string; applicationId: string };

type SignerAction = 'unlock' | 'resendEmail' | 'resendSms';

const signerMutationConfig = {
  unlock: {
    successMessage: 'notifications.signerUnlocked',
    errorMessage: 'notifications.signerUnlockFailed',
  },
  resendEmail: {
    successMessage: 'notifications.signerEmailResent',
    errorMessage: 'notifications.signerEmailResendFailed',
  },
  resendSms: {
    successMessage: 'notifications.signerSmsResent',
    errorMessage: 'notifications.signerSmsResendFailed',
  },
} as const;

function createSignerMutation(action: SignerAction) {
  return function useSignerMutation(
    options?: UseMutationOptions<ESignSigner | null, AxiosError, SignerActionBase>,
  ): UseMutationResult<ESignSigner | null, AxiosError, SignerActionBase> {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const config = signerMutationConfig[action];

    return useMutation(({ signerId, applicationId }) => eSignSignerAction({ action, signerId, applicationId }), {
      ...options,
      onSuccess: async (data, variables, context) => {
        dispatch(notificationSlice.actions.setSuccess({ message: t(config.successMessage) }));
        await queryClient.invalidateQueries([QueryId.eSignCeremony, variables.applicationId]);
        await options?.onSuccess?.(data, variables, context);
      },
      onError: async (error, variables, context) => {
        dispatch(notificationSlice.actions.setError({ message: t(config.errorMessage) }));
        await options?.onError?.(error, variables, context);
      },
    });
  };
}

export const useUnlockSignerMutation = createSignerMutation('unlock');
export const useResendEmailMutation = createSignerMutation('resendEmail');
export const useResendSmsMutation = createSignerMutation('resendSms');

type EditSignerParams = SignerActionBase & {
  email?: string;
  cellphone?: string;
  passcode?: string;
  authMethod?: ESignSignerAuthMethod;
};

export function useEditSignerMutation(
  options?: UseMutationOptions<ESignSigner | null, AxiosError, EditSignerParams>,
): UseMutationResult<ESignSigner | null, AxiosError, EditSignerParams> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation<ESignSigner | null, AxiosError, EditSignerParams>(
    (params) => eSignSignerAction({ action: 'editSigner', ...params }),
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        dispatch(notificationSlice.actions.setSuccess({ message: t('notifications.signerUpdated') }));
        await queryClient.invalidateQueries([QueryId.eSignCeremony, variables.applicationId]);
        await options?.onSuccess?.(data, variables, context);
      },
      onError: async (error, variables, context) => {
        dispatch(notificationSlice.actions.setError({ message: t('notifications.signerUpdateFailed') }));
        await options?.onError?.(error, variables, context);
      },
    },
  );
}
